import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  LayoutDashboard,
  Users,
  Send,
  Activity as ActivityIcon,
} from "lucide-react";

import DashboardSidebar from "../../agencyDashboardSidebar";
import PipelineTabs, { Tab } from "../../_shared/PipelineTabs";
import {
  getJobById,
  getJobMetadata,
} from "../../../../../services/staffingService";

import OverviewTab from "./OverviewTab";
import RecommendationsTab from "./RecommendationsTab";
import SentOffersTab from "./SentOffersTab";
import ActivityTab from "./ActivityTab";

/**
 * Job Detail — pipeline shell.
 * Route: /provider-company/:providerCompanyId/jobs/:jobId
 * Tabs (Bullhorn pipeline pattern):
 *   Overview | Recommendations | Sent Offers | Activity
 *
 * Active tab is URL-driven via ?tab=overview|recommendations|offers|activity
 * to allow deep-linking (back-compat alias for the old /offer routes).
 */
const TAB_KEYS = ["overview", "recommendations", "offers", "activity"];

const JobDetail = () => {
  const { providerCompanyId, jobId } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [showSideDrawer, setShowSideDrawer] = useState(false);
  const [job, setJob] = useState(null);
  const [shifts, setShifts] = useState([]);
  const [offersMetadata, setOffersMetadata] = useState([]);
  const [officersCount, setOfficersCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Derive active tab from URL ?tab=…
  const params = new URLSearchParams(location.search);
  const tabParam = params.get("tab");
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : "overview";

  const setActiveTab = (next) => {
    const sp = new URLSearchParams(location.search);
    sp.set("tab", next);
    history.replace({ pathname: location.pathname, search: sp.toString() });
  };

  // ── Fetch job + offer metadata in parallel ────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [jobResp, metaResp] = await Promise.all([
        getJobById(providerCompanyId, jobId),
        getJobMetadata(providerCompanyId, jobId),
      ]);
      setJob(jobResp.data.job);
      setShifts(jobResp.data.shifts || []);
      setOffersMetadata(metaResp.data.offers || []);
      setOfficersCount(metaResp.data.totalRecommendations || 0);
    } catch (err) {
      console.error("Error loading job detail:", err);
    } finally {
      setLoading(false);
    }
  }, [providerCompanyId, jobId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const companyName =
    job?.customer_company_local_id?.companyFriendlyName ||
    job?.customer_company_local_id?.companyLegalName ||
    job?.customer_company_id?.companyName ||
    "Work Order";

  const address = job?.formattedAddress || "";

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-inter">
      <DashboardSidebar
        isOpen={showSideDrawer}
        setIsOpen={setShowSideDrawer}
      />
      <main className="flex-1 overflow-y-auto">
        <div className="p-6 h-full flex flex-col">
          {/* Back Button — Dispoline pattern */}
          <button
            onClick={() =>
              history.push(`/provider-company/${providerCompanyId}/jobs`)
            }
            className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-fit"
          >
            <ArrowLeft size={16} />
            Back to Jobs
          </button>

          {/* Page Header — Dispoline pattern */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-foreground">
              {loading && !job ? "Loading…" : companyName}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {address || "Manage recommendations, offers, and activity for this work order"}
            </p>
          </div>

          {/* Pipeline tabs */}
          <div className="flex-1 min-h-0">
            <PipelineTabs
              activeKey={activeTab}
              onSelect={setActiveTab}
              className="h-full"
            >
              <Tab
                eventKey="overview"
                label="Overview"
                icon={LayoutDashboard}
              >
                <OverviewTab
                  job={job}
                  shifts={shifts}
                  offersMetadata={offersMetadata}
                  loading={loading}
                />
              </Tab>

              <Tab
                eventKey="recommendations"
                label="Recommendations"
                icon={Users}
                count={officersCount}
              >
                <RecommendationsTab
                  providerCompanyId={providerCompanyId}
                  jobId={jobId}
                  setOfficersCount={setOfficersCount}
                  officersCount={officersCount}
                  fetchJobAndOfferMetadata={fetchAll}
                  goToOffers={() => setActiveTab("offers")}
                />
              </Tab>

              <Tab
                eventKey="offers"
                label="Sent Offers"
                icon={Send}
                count={offersMetadata.length}
              >
                <SentOffersTab
                  providerCompanyId={providerCompanyId}
                  jobId={jobId}
                  offersMetadata={offersMetadata}
                />
              </Tab>

              <Tab
                eventKey="activity"
                label="Activity"
                icon={ActivityIcon}
              >
                <ActivityTab
                  providerCompanyId={providerCompanyId}
                  jobId={jobId}
                />
              </Tab>
            </PipelineTabs>
          </div>
        </div>
      </main>

      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-pink mx-auto" />
            <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetail;
